import { useCallback, useMemo } from 'react'

import { useServiceGroups } from '@/api/hooks/useServiceGroups'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { SettingsActions } from '@/redux/reducers/settings'
import { metChange } from '@/utils/service-diff.utils'

/**
 * Whether Hitta stöd has changes this user hasn't looked at yet.
 *
 * The underlying `metChange` diff is derived from `lastRun`, which only says
 * what moved between the last two generations — it carries no notion of having
 * been read, so on its own the nav dot stays lit until some later regen happens
 * to land on the same value. Pairing it with a locally-stored "last seen"
 * timestamp (persisted per individual via redux-persist → localStorage) is what
 * lets opening the page actually clear it.
 *
 * Deliberately local: this is a per-device reading position, not shared state.
 */
export const useServiceChanges = (individualId?: string) => {
  const { data: serviceGroups = [] } = useServiceGroups(individualId)
  const lastSeenIso = useAppSelector(state =>
    individualId ? state.settings.servicesLastSeen?.[individualId] : undefined,
  )

  // The newest generation timestamp among groups whose met actually changed.
  // Stamping this (rather than "now") on read means a regen that lands while the
  // page is open is still covered, without hiding one that arrives afterwards.
  const latestChangeAt = useMemo(() => {
    let latest = 0

    for (const group of serviceGroups) {
      const generation = group.generation
      if (!generation) continue
      if (metChange(generation.met, generation.lastRun?.met)?.kind !== 'changed') continue
      const at = Date.parse(generation.updatedAt)
      if (!Number.isNaN(at) && at > latest) latest = at
    }

    return latest
  }, [serviceGroups])

  const lastSeen = lastSeenIso ? Date.parse(lastSeenIso) : 0

  return {
    hasUnseenChanges: latestChangeAt > 0 && latestChangeAt > lastSeen,
    latestChangeAt,
  }
}

/** Records that the user has seen the current Hitta stöd changes. */
export const useMarkServicesSeen = () => {
  const dispatch = useAppDispatch()

  return useCallback(
    (individualId: string, at: number) => {
      dispatch(
        SettingsActions.markServicesSeen({ at: new Date(at).toISOString(), individualId }),
      )
    },
    [dispatch],
  )
}
